/**
 * SecOps data hooks — alerts, incidents, playbooks, threat hunting, response actions, dark web, shadow AI
 */

import { useState, useEffect, useCallback } from 'react';
import { secopsApi } from '../lib/secops-api';

/* ─── Generic polling hook ─── */

function useSecOpsPolling<T>(path: string, intervalMs: number, enabled = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await secopsApi.get<T>(path);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => {
    fetch();
    if (intervalMs <= 0 || !enabled) return;
    const timer = setInterval(fetch, intervalMs);
    return () => clearInterval(timer);
  }, [fetch, intervalMs, enabled]);

  return { data, loading, error, refresh: fetch };
}

function buildQS(filters: Record<string, string> = {}): string {
  const params = new URLSearchParams(filters).toString();
  return params ? `?${params}` : '';
}

/* ─── SIEM Alerts ─── */

export function useSIEMAlerts(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ alerts: unknown[]; total: number }>(
    `/secops/alerts${buildQS(filters)}`, 15_000,
  );
}

export function useSIEMAlert(id: string | null) {
  return useSecOpsPolling<Record<string, unknown>>(
    `/secops/alerts/${id}`, 0, !!id,
  );
}

/* ─── Incidents ─── */

export function useIncidents(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ incidents: unknown[]; total: number }>(
    `/secops/incidents${buildQS(filters)}`, 15_000,
  );
}

export function useIncident(id: string | null) {
  return useSecOpsPolling<Record<string, unknown>>(
    `/secops/incidents/${id}`, 0, !!id,
  );
}

export interface IncidentTimelineEntry {
  id: string;
  event_type: string;
  description: string;
  actor_id: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export function useIncidentTimeline(id: string | null) {
  return useSecOpsPolling<{ data: IncidentTimelineEntry[] }>(
    `/secops/incidents/${id}?action=timeline`, 30_000, !!id,
  );
}

/* ─── Playbooks ─── */

export function usePlaybooks(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ playbooks: unknown[]; total: number }>(
    `/secops/playbooks${buildQS(filters)}`, 30_000,
  );
}

export function usePlaybook(id: string | null) {
  return useSecOpsPolling<Record<string, unknown>>(
    `/secops/playbooks/${id}`, 0, !!id,
  );
}

/* ─── Threat Hunting ─── */

export function useThreatHunts(filters: Record<string, string> = {}) {
  const qs = buildQS({ action: 'list_hunts', ...filters });
  return useSecOpsPolling<{ hunts: unknown[]; total: number }>(
    `/secops/threat-hunting${qs}`, 30_000,
  );
}

export function useThreatHunt(id: string | null) {
  return useSecOpsPolling<Record<string, unknown>>(
    `/secops/threat-hunting?action=get_hunt&id=${id}`, 0, !!id,
  );
}

/* ─── SIEM Config ─── */

export function useSIEMConfigs() {
  return useSecOpsPolling<{ configs: unknown[] }>(
    '/secops/siem-config', 60_000,
  );
}

/* ─── Response Actions ─── */

export function useResponseActions(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ actions: unknown[]; total: number }>(
    `/secops/response-actions${buildQS(filters)}`, 15_000,
  );
}

/* ─── Dark Web Monitoring ─── */

export function useDarkWebMonitors() {
  return useSecOpsPolling<{ monitors: unknown[]; findings: unknown[] }>(
    '/secops/dark-web?action=list', 60_000,
  );
}

/* ─── Shadow AI ─── */

export function useShadowAI(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ discoveries: unknown[]; total: number; stats: Record<string, unknown> }>(
    `/secops/shadow-ai${buildQS(filters)}`, 30_000,
  );
}

/* ─── SOC Dashboard Summary ─── */

export interface SOCDashboardData {
  executive: {
    total_alerts_30d: number;
    critical_alerts: number;
    high_alerts: number;
    open_incidents: number;
    resolved_incidents_30d: number;
    avg_mttd_minutes: number | null;
    avg_mttr_minutes: number | null;
    security_score: number | null;
  };
  score_trend: Array<{ score: number; date: string }>;
  top_categories: Array<{ category: string; count: number }>;
  mitre_hits: Record<string, number>;
  alert_sources: Record<string, number>;
  timestamp: string;
}

export function useSOCDashboard() {
  return useSecOpsPolling<{ data: SOCDashboardData }>(
    '/secops/soc-dashboard', 30_000,
  );
}

/* ─── Mutation functions (not hooks) ─── */

// Alerts
export const createAlert = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/alerts', data);

export const updateAlert = (id: string, data: Record<string, unknown>) =>
  secopsApi.put<void>(`/secops/alerts/${id}`, data);

export const deleteAlert = (id: string) =>
  secopsApi.delete<void>(`/secops/alerts/${id}`);

// Incidents
export const createIncident = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/incidents', data);

export const updateIncident = (id: string, data: Record<string, unknown>) =>
  secopsApi.put<void>(`/secops/incidents/${id}`, data);

// Playbooks
export const createPlaybook = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/playbooks', data);

export const activatePlaybook = (data: { playbook_id: string; incident_id: string }) =>
  secopsApi.post<{ execution_id: string }>('/secops/playbooks/activate', data);

export const aiGeneratePlaybook = (data: { context: string; severity_focus?: string; incident_id?: string }) =>
  secopsApi.post<{ data: Record<string, unknown> }>('/secops/playbooks/ai-generate', data);

export const playbookFromIncident = (incidentId: string) =>
  secopsApi.post<{ data: Record<string, unknown> }>(`/secops/playbooks/from-incident/${incidentId}`, {});

// Threat Hunting
export const createThreatHunt = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/threat-hunting', data);

export const startHunt = (id: string) =>
  secopsApi.post<void>('/secops/threat-hunting', { action: 'start', id });

export const completeHunt = (id: string, data: Record<string, unknown>) =>
  secopsApi.post<void>('/secops/threat-hunting', { action: 'complete', id, ...data });

// Response Actions
export const executeResponseAction = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/response-actions', data);

// Dark Web
export const addDarkWebMonitor = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/dark-web', data);

export const removeDarkWebMonitor = (id: string) =>
  secopsApi.post<void>(`/secops/dark-web/${id}`, { action: 'remove_monitor' });

export const checkHibp = (monitorId: string) =>
  secopsApi.post<{ new_breaches: number; new_pastes: number }>(`/secops/dark-web/${monitorId}`, { action: 'check_hibp' });

export const acknowledgeDarkWebFinding = (findingId: string) =>
  secopsApi.post<void>(`/secops/dark-web/${findingId}`, { action: 'resolve_finding', status: 'acknowledged' });

export const resolveDarkWebFinding = (findingId: string) =>
  secopsApi.post<void>(`/secops/dark-web/${findingId}`, { action: 'resolve_finding', status: 'resolved' });

// Shadow AI
export const reportShadowAI = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/shadow-ai', { action: 'report', ...data });

export const approveShadowAI = (id: string, data: Record<string, unknown>) =>
  secopsApi.post<void>('/secops/shadow-ai', { action: 'approve', id, ...data });

export const blockShadowAI = (id: string, data: Record<string, unknown>) =>
  secopsApi.post<void>('/secops/shadow-ai', { action: 'block', id, ...data });

// SIEM Config
export const saveSIEMConfig = (data: Record<string, unknown>) =>
  secopsApi.post<{ id: string }>('/secops/siem-config', data);

export const testSIEMConnection = (id: string) =>
  secopsApi.post<{ success: boolean; message: string }>('/secops/siem-config/test', { id });

export const toggleSIEMSource = (id: string, enabled: boolean) =>
  secopsApi.put<void>(`/secops/siem-config/${id}`, { enabled });

/* ─── Correlation hooks ─── */

export interface CorrelationDashboardData {
  active_correlations: Array<{
    id: string;
    title: string;
    description: string;
    severity: string;
    status: string;
    correlation_rule_id: string;
    correlation_confidence: number;
    correlation_sources: string[];
    kill_chain_phases: string[];
    mitre_techniques: string[];
    affected_assets: string[];
    created_at: string;
  }>;
  kill_chain: {
    stages: Array<{
      phase: string;
      label: string;
      mitre_tactic: string;
      detected: boolean;
      incident_count: number;
      rules_matched: string[];
    }>;
    coverage_percentage: number;
    total_detected: number;
  };
  stats: {
    total_active: number;
    critical: number;
    high: number;
    avg_confidence: number;
    by_rule: Record<string, number>;
    by_source: Record<string, number>;
  };
  rules: Array<{
    id: string;
    name: string;
    mitre_techniques: string[];
    severity: string;
    kill_chain_phase: string;
  }>;
  generated_at: string;
}

export function useCorrelationDashboard() {
  return useSecOpsPolling<{ data: CorrelationDashboardData }>(
    '/secops/correlations', 30_000,
  );
}

export const runCorrelationNow = () =>
  secopsApi.post<{ data: { matches: unknown[]; incidents_created: number; execution_time_ms: number } }>('/secops/correlations', { action: 'run_now' });

/* ─── Escalation hooks ─── */

export interface EscalationConfig {
  auto_escalate: boolean;
  p1_delay_minutes: number;
  p2_delay_minutes: number;
  psa_board_id: string;
  oncall_schedule_id: string;
  notify_emails: string[];
  slack_webhook_url?: string;
  psa_create_tickets: boolean;
}

export interface EscalationMetrics {
  period_days: number;
  total: number;
  pending: number;
  acknowledged: number;
  resolved: number;
  avg_mtta_minutes: number | null;
  avg_mttr_minutes: number | null;
  by_level: Record<string, number>;
  history: Array<{
    id: string;
    incident_id: string;
    incident_title: string;
    escalation_level: string;
    status: string;
    triggered_at: string;
    acknowledged_at?: string;
    mtta_minutes?: number;
    mttr_minutes?: number;
  }>;
}

export interface ActiveEscalation {
  id: string;
  incident_id: string;
  incident_title: string;
  incident_severity: string;
  escalation_level: string;
  status: string;
  triggered_by: string;
  triggered_at: string;
  oncall_alert_id?: string;
  psa_ticket_id?: string;
}

export function useEscalationConfig() {
  return useSecOpsPolling<{ data: EscalationConfig }>(
    '/secops/escalation-config', 0,
  );
}

export function useActiveEscalations() {
  return useSecOpsPolling<{ data: ActiveEscalation[] }>(
    '/secops/escalation/active', 30_000,
  );
}

export function useEscalationMetrics() {
  return useSecOpsPolling<{ data: EscalationMetrics }>(
    '/secops/escalation/metrics', 60_000,
  );
}

export const saveEscalationConfig = (data: Partial<EscalationConfig>) =>
  secopsApi.put<{ data: EscalationConfig }>('/secops/escalation-config', data);

export const triggerEscalation = (data: { incident_id: string; override_level?: string; override_severity?: string }) =>
  secopsApi.post<{ data: ActiveEscalation }>('/secops/escalation/trigger', data);

export const resolveEscalation = (id: string) =>
  secopsApi.post<{ data: ActiveEscalation }>(`/secops/escalation/${id}/resolve`, {});

/* ─── Autonomous Response Config ─── */

export interface AutonomousResponseConfig {
  autonomous_response_enabled: boolean;
  autonomous_severity_threshold: 'critical' | 'high';
  autonomous_excluded_playbooks: string[];
  autonomous_require_backup_verify: boolean;
  updated_at: string;
  updated_by: string;
}

export function useAutonomousConfig() {
  return useSecOpsPolling<{ data: AutonomousResponseConfig }>(
    '/secops/siem-config?action=get_autonomous_config', 0,
  );
}

export const saveAutonomousConfig = (data: Partial<AutonomousResponseConfig>) =>
  secopsApi.post<{ data: AutonomousResponseConfig }>('/secops/siem-config', { action: 'set_autonomous_config', ...data });

/* ─── Incident Context (Cross-Product Enrichment) ─── */

export interface IncidentContext {
  customer?: { company_name?: string; primary_contact?: string; sla_tier?: string; source: string };
  assets?: Array<{ id: string; name?: string; type?: string; os?: string; status?: string }>;
  rmm_status?: { devices_online: number; devices_offline: number; recent_alerts: number; source: string };
  incident_history?: Array<{ id: string; title: string; severity: string; status: string; classification?: string; opened_at: string; closed_at?: string }>;
  compliance?: { breach_notification_deadline?: string; applicable_regulations?: string[]; notification_required: boolean };
  time_entries?: { total_minutes: number; entry_count: number; billable_minutes: number };
}

export function useIncidentContext(id: string | null) {
  return useSecOpsPolling<{ data: IncidentContext }>(
    `/secops/incidents/${id}/context`, 0, !!id,
  );
}

/* ─── Incident Time Entries ─── */

export interface TimeEntry {
  id: string;
  incident_id: string;
  technician_id: string;
  technician_name: string;
  duration_minutes: number;
  description?: string;
  billable: boolean;
  worked_at: string;
  created_at: string;
}

export function useIncidentTimeEntries(id: string | null) {
  return useSecOpsPolling<{ data: TimeEntry[]; total_minutes: number }>(
    `/secops/incidents/${id}/time-entries`, 0, !!id,
  );
}

export const createTimeEntry = (incidentId: string, data: { duration_minutes: number; description?: string; billable?: boolean }) =>
  secopsApi.post<{ data: TimeEntry }>(`/secops/incidents/${incidentId}/time-entries`, data);

/* ─── SOC Analytics ─── */

export interface SOCAnalyticsData {
  summary: {
    total_alerts_90d: number;
    total_incidents_90d: number;
    open_alerts: number;
    open_incidents: number;
    false_positive_rate: number;
    avg_mttd_minutes: number | null;
    avg_mttr_minutes: number | null;
  };
  alert_volume_trend: Array<{ week: string; total: number; critical: number; high: number; medium: number; low: number }>;
  response_trend: Array<{ week: string; avg_mttd: number | null; avg_mttr: number | null }>;
  severity_breakdown: { critical: number; high: number; medium: number; low: number; info: number };
  analyst_workload: Array<{ analyst: string; open_alerts: number; open_incidents: number; resolved_90d: number }>;
  rule_effectiveness: Array<{ rule_id: string; hits: number; false_positives: number; precision: number }>;
  source_reliability: Array<{ source: string; total_alerts: number; false_positive_rate: number; reliability_score: number }>;
  incident_classifications: Record<string, number>;
  generated_at: string;
}

export function useSOCAnalytics() {
  return useSecOpsPolling<{ data: SOCAnalyticsData }>(
    '/secops/analytics', 60_000,
  );
}

/* ─── Detection Rules ─── */

export interface DetectionRule {
  id: string;
  name: string;
  description: string;
  rule_type: string;
  severity: string;
  kql_query: string;
  lookback_duration: string;
  run_interval: string;
  mitre_tactics: string[];
  mitre_techniques: string[];
  tags: string[];
  category?: string;
  is_enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_run_at?: string;
  last_hit_at?: string;
  false_positive_rate_30d?: number;
}

export function useDetectionRules() {
  return useSecOpsPolling<{ data: DetectionRule[]; total: number }>(
    '/secops/detection-rules', 30_000,
  );
}

export function useDetectionRule(id: string | null) {
  return useSecOpsPolling<{ data: DetectionRule }>(
    `/secops/detection-rules/${id}`, 0, !!id,
  );
}

export const createDetectionRule = (data: Record<string, unknown>) =>
  secopsApi.post<{ data: DetectionRule }>('/secops/detection-rules', data);

export const updateDetectionRule = (id: string, data: Record<string, unknown>) =>
  secopsApi.put<{ data: DetectionRule }>(`/secops/detection-rules/${id}`, data);

export const deleteDetectionRule = (id: string) =>
  secopsApi.delete<void>(`/secops/detection-rules/${id}`);

export const testDetectionRule = (kql: string, lookbackDuration?: string) =>
  secopsApi.post<{ success: boolean; match_count: number; results: unknown[]; error?: string; columns?: string[]; execution_time_ms?: number }>(
    '/secops/detection-rules', { action: 'test', kql_query: kql, lookback_duration: lookbackDuration },
  );

/* ─── Cross-Client Threats ─── */

export interface CrossClientData {
  platform_threats: Array<{
    ioc_value: string;
    ioc_type: string;
    tenant_count: number;
    severity: string;
    first_seen: string;
    last_seen: string;
    mitre_techniques: string[];
    tags: string[];
    detected_in_this_tenant: boolean;
  }>;
  local_matches: Array<{
    alert_id: string;
    alert_title: string;
    severity: string;
    ioc_matched: string;
    detected_at: string;
  }>;
  correlation_incidents: Array<{
    incident_id: string;
    title: string;
    severity: string;
    confidence: number;
    affected_assets: string[];
    created_at: string;
  }>;
  summary: {
    total_platform_threats: number;
    threats_in_this_tenant: number;
    total_affected_orgs: number;
    corr_008_incidents: number;
  };
}

export function useCrossClientThreats() {
  return useSecOpsPolling<{ data: CrossClientData }>(
    '/secops/cross-client-threats', 60_000,
  );
}

/* ─── SOC Benchmarking ─── */

export interface BenchmarkComparison {
  yours: number | null;
  community_avg: number;
  community_p50?: number;
  delta_pct: number | null;
  ranking: { percentile: number; label: string } | null;
}

export interface SOCBenchmarkData {
  local: {
    avg_mttd_minutes: number | null;
    avg_mttr_minutes: number | null;
    p50_mttd_minutes: number | null;
    p50_mttr_minutes: number | null;
    false_positive_rate: number;
    avg_alerts_per_week: number;
    avg_incidents_per_week: number;
    total_alerts: number;
    total_incidents: number;
  };
  community: {
    avg_mttd_minutes: number;
    avg_mttr_minutes: number;
    false_positive_rate: number;
    avg_alerts_per_week: number;
    avg_incidents_per_week: number;
    p50_mttd_minutes: number;
    p50_mttr_minutes: number;
    sample_size: number;
    updated_at: string;
  };
  comparisons: {
    mttd: BenchmarkComparison;
    mttr: BenchmarkComparison;
    false_positive_rate: BenchmarkComparison;
    alert_volume: { yours: number; community_avg: number };
    incident_volume: { yours: number; community_avg: number };
  };
  collective_configured: boolean;
  sample_size: number;
}

export function useSOCBenchmarks() {
  return useSecOpsPolling<{ data: SOCBenchmarkData }>(
    '/secops/benchmarking', 300_000, // 5-minute refresh
  );
}

/* ─── Threat Modeling ─── */

export interface ThreatPrediction {
  attack_vector: string;
  likelihood_score: number;
  mitre_techniques: string[];
  description: string;
  recommendation: string;
  risk_multiplier?: string;
}

export interface RiskProfile {
  overall_score: number;
  top_risk_category: string;
  industry?: string;
  compliance_score?: number;
  coverage_gaps: string[];
  strengths: string[];
}

export interface ThreatModelData {
  predictions: ThreatPrediction[];
  risk_profile: RiskProfile;
  generated_at: string;
  context_summary: string;
}

export function useThreatModel() {
  return useSecOpsPolling<{ data: ThreatModelData }>(
    '/secops/threat-model', 0, // No polling — regenerate manually
  );
}

export const regenerateThreatModel = () =>
  secopsApi.post<{ data: ThreatModelData }>('/secops/threat-model', {});

/* ─── UEBA (User & Entity Behavior Analytics) ─── */

export interface UEBAEntity {
  id: string;
  type: 'ueba_entity';
  entity_type: 'user' | 'device';
  entity_name: string;
  entity_id: string;
  risk_score: number;
  anomaly_count: number;
  last_seen: string;
  baseline: {
    normal_login_hours: { start: number; end: number };
    avg_daily_access_count: number;
    avg_daily_network_bytes: number;
    systems_accessed: string[];
    computed_at: string;
    sample_days: number;
  } | null;
  anomalies: UEBAAnomaly[];
  updated_at: string;
}

export interface UEBAAnomaly {
  id: string;
  anomaly_type: 'unusual_hours' | 'volume_spike' | 'new_system_access' | 'lateral_movement' | 'privilege_escalation';
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  risk_contribution: number;
  detected_at: string;
  metadata?: Record<string, unknown>;
  entity_id?: string;
  entity_name?: string;
  entity_type?: string;
}

export function useUEBAEntities(filters: Record<string, string> = {}) {
  return useSecOpsPolling<{ data: { entities: UEBAEntity[]; total: number } }>(
    `/secops/ueba/entities${buildQS(filters)}`, 30_000,
  );
}

export function useUEBAAnomaly() {
  return useSecOpsPolling<{ data: { anomalies: (UEBAAnomaly & { entity_name?: string; entity_type?: string })[]; total: number } }>(
    '/secops/ueba/anomalies', 30_000,
  );
}

/* ─── Investigation Graph ─── */

export interface InvestigationGraphNode {
  id: string;
  type: 'user' | 'ip' | 'device' | 'process' | 'file';
  label: string;
  properties: Record<string, string>;
  risk_score?: number;
}

export interface InvestigationGraphEdge {
  source: string;
  target: string;
  label: string;
  timestamp?: string;
  count?: number;
}

export interface SavedInvestigation {
  id: string;
  name: string;
  description?: string;
  root_entity: { type: string; value: string };
  node_count: number;
  edge_count: number;
  created_by: string;
  created_at: string;
}

export const buildInvestigationGraph = (data: { root_entity: { type: string; value: string }; depth?: number }) =>
  secopsApi.post<{ nodes: InvestigationGraphNode[]; edges: InvestigationGraphEdge[] }>('/secops/investigation/graph', data);

export const expandInvestigationNode = (data: { node_id: string; node_type: string; node_value: string }) =>
  secopsApi.post<{ nodes: InvestigationGraphNode[]; edges: InvestigationGraphEdge[] }>('/secops/investigation/expand', data);

export function useSavedInvestigations() {
  return useSecOpsPolling<{ data: SavedInvestigation[] }>(
    '/secops/investigation/saved', 0,
  );
}

export const saveInvestigationGraph = (data: {
  name: string;
  description?: string;
  nodes: InvestigationGraphNode[];
  edges: InvestigationGraphEdge[];
  root_entity: { type: string; value: string };
}) =>
  secopsApi.post<{ data: SavedInvestigation }>('/secops/investigation/save', data);

/* ─── Hunt Marketplace ─── */

export interface MarketplaceHunt {
  id: string;
  type: 'marketplace_hunt';
  name: string;
  description: string;
  kql_query: string;
  mitre_techniques: string[];
  severity: string;
  tags: string[];
  author_anonymous: boolean;
  effectiveness_rating: number;
  true_positive_rate: number;
  total_runs: number;
  total_ratings: number;
  environments_detected: number;
  source: 'local' | 'collective';
  imported_at?: string;
  created_at: string;
  updated_at: string;
}

export function useHuntMarketplace() {
  return useSecOpsPolling<{ data: MarketplaceHunt[]; collective_configured: boolean }>(
    '/secops/hunt-marketplace', 60_000,
  );
}

export const submitHuntToMarketplace = (data: {
  name: string;
  description: string;
  kql_query: string;
  severity?: string;
  tags?: string[];
  share_to_collective?: boolean;
}) =>
  secopsApi.post<{ data: MarketplaceHunt }>('/secops/hunt-marketplace', data);

export const marketplaceHuntAction = (id: string, action: string, rating?: number) =>
  secopsApi.post<{ data: MarketplaceHunt }>(`/secops/hunt-marketplace/${id}`, { action, rating });

/* ─── Playbook Builder (AI Pattern Analysis) ─── */

export interface PlaybookSuggestion {
  name: string;
  description: string;
  classification: string;
  severity_trigger: string[];
  incident_count: number;
  common_steps: string[];
  estimated_duration_minutes: number;
  confidence: number;
}

export function usePlaybookSuggestions() {
  return useSecOpsPolling<{ data: { suggestions: PlaybookSuggestion[]; total_incidents_analyzed: number } }>(
    '/secops/playbook-builder/analyze', 0, // No auto-refresh — triggered manually
  );
}

export const generatePlaybookFromSuggestion = (data: {
  name: string;
  description: string;
  classification: string;
  severity_trigger: string[];
  steps: string[];
}) =>
  secopsApi.post<{ data: Record<string, unknown> }>('/secops/playbook-builder/generate', data);

/* ─── SOC Autopilot ─── */

export interface AutopilotConfig {
  enabled: boolean;
  confidence_threshold: number;
  auto_close_false_positives: boolean;
  auto_escalate_confirmed: boolean;
  excluded_categories: string[];
  human_review_required_above: 'high' | 'critical';
  updated_at: string;
  updated_by: string;
}

export interface AutopilotDecision {
  id: string;
  alert_id: string;
  alert_title?: string;
  alert_severity?: string;
  decision: 'close_fp' | 'escalate' | 'investigate' | 'hold';
  confidence: number;
  reasoning: string;
  ai_model: string;
  status: 'applied' | 'overridden' | 'pending_review';
  override_action?: 'approve' | 'reject';
  override_reason?: string;
  override_by?: string;
  override_at?: string;
  timestamp: string;
}

export interface AutopilotActivity {
  decisions: AutopilotDecision[];
  total: number;
  stats: {
    auto_closed_today: number;
    escalated_today: number;
    pending_review: number;
    override_rate: number;
  };
}

export function useAutopilotConfig() {
  return useSecOpsPolling<{ data: AutopilotConfig }>(
    '/secops/autopilot/config', 0,
  );
}

export function useAutopilotActivity() {
  return useSecOpsPolling<{ data: AutopilotActivity }>(
    '/secops/autopilot/activity', 15_000,
  );
}

export const saveAutopilotConfig = (data: Partial<AutopilotConfig>) =>
  secopsApi.put<{ data: AutopilotConfig }>('/secops/autopilot/config', data);

export const overrideAutopilotDecision = (data: { decision_id: string; action: 'approve' | 'reject'; reason?: string }) =>
  secopsApi.post<{ data: AutopilotDecision }>('/secops/autopilot/override', data);

/* ─── Attack Simulation ─── */

export interface AttackScenario {
  id: string;
  name: string;
  mitre: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  script_type: string;
  category: string;
}

export interface AttackSimRun {
  id: string;
  scenario_id: string;
  scenario_name: string;
  mitre_technique: string;
  risk_level: string;
  target_device_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at?: string;
  detection_time_ms?: number;
  detected: boolean;
  detection_rule_id?: string;
  detection_alert_id?: string;
  error_message?: string;
  created_by: string;
}

export interface AttackSimCoverage {
  total_scenarios: number;
  tested: number;
  detected: number;
  coverage_pct: number;
}

export function useAttackScenarios() {
  return useSecOpsPolling<{ data: AttackScenario[] }>(
    '/secops/attack-sim/scenarios', 0,
  );
}

export function useAttackSimRuns() {
  return useSecOpsPolling<{ data: { runs: AttackSimRun[]; total: number; coverage: AttackSimCoverage } }>(
    '/secops/attack-sim/runs', 15_000,
  );
}

export const startAttackSim = (data: { scenario_id: string; target_device_id: string }) =>
  secopsApi.post<{ data: AttackSimRun }>('/secops/attack-sim/run', data);

export const getAttackSimRun = (id: string) =>
  secopsApi.get<{ data: AttackSimRun }>(`/secops/attack-sim/runs/${id}`);

/* ─── Large Context SIEM Analysis ─── */

export interface ThreatFinding {
  severity: 'critical' | 'high' | 'medium' | 'low';
  type: string;
  description: string;
  affected_devices: string[];
  first_seen: string;
  last_seen: string;
  event_count: number;
  mitre_technique?: string;
  recommended_action: string;
}

export interface AnomalyFinding {
  description: string;
  affected_device: string;
  baseline_deviation: string;
  first_occurrence: string;
}

export interface SiemAnalysisResult {
  threats: ThreatFinding[];
  anomalies: AnomalyFinding[];
  compliance_gaps: string[];
  summary: string;
  log_volume_analyzed: number;
  time_range_analyzed: { start: string; end: string };
  recommendations: string[];
}

export const runLogAnalysis = (data: {
  clientId: string;
  startTime: string;
  endTime: string;
  analysisType: string;
  deviceIds?: string[];
  incidentContext?: string;
}) =>
  secopsApi.post<SiemAnalysisResult>('/security/siem/analyze', data);

export const runIncidentLogAnalysis = (data: {
  clientId: string;
  incidentId: string;
  hoursBack?: number;
}) =>
  secopsApi.post<SiemAnalysisResult>('/security/siem/analyze-incident', data);

/* ─── Deception Technology ─── */

export interface DeceptionAsset {
  id: string;
  decoy_type: 'honey_file' | 'honey_cred' | 'honey_service' | 'honey_share';
  target_device_id: string;
  config: Record<string, unknown>;
  status: 'deploying' | 'active' | 'triggered' | 'removed' | 'failed';
  trigger_count: number;
  deployed_at: string;
  deployed_by: string;
  removed_at?: string;
}

export interface DeceptionAlertItem {
  id: string;
  decoy_id: string;
  decoy_type: string;
  target_device_id: string;
  attacker_ip?: string;
  attacker_process?: string;
  attacker_user?: string;
  details: string;
  severity: string;
  timestamp: string;
}

export interface DeceptionStats {
  total: number;
  active: number;
  triggered_this_month: number;
  false_positive_rate: number;
}

export function useDeceptionAssets() {
  return useSecOpsPolling<{ data: { assets: DeceptionAsset[]; stats: DeceptionStats } }>(
    '/secops/deception/assets', 30_000,
  );
}

export function useDeceptionAlerts() {
  return useSecOpsPolling<{ data: { alerts: DeceptionAlertItem[]; total: number } }>(
    '/secops/deception/alerts', 15_000,
  );
}

export const deployDecoy = (data: { type: string; target_device_id: string; config?: Record<string, unknown> }) =>
  secopsApi.post<{ data: DeceptionAsset }>('/secops/deception/deploy', data);

export const removeDecoy = (id: string) =>
  secopsApi.delete<{ data: DeceptionAsset }>(`/secops/deception/assets/${id}`);

// ─── ARGUS Integration ──────────────────────────────────────────────────────

export interface ArgusInvestigation {
  findings: string[];
  indicators: string[];
  verdict: string;
  full_analysis: string;
}

export interface ArgusGeneratedRule {
  threat_id: string;
  rule_type: string;
  rule: string;
  description: string;
}

export const investigateIncident = (incidentId: string) =>
  secopsApi.post<{ incident: Record<string, unknown>; investigation: ArgusInvestigation }>(
    `/security/incidents/${incidentId}/investigate`,
    {},
  );

export const generateThreatRule = (threatId: string, ruleType: 'yara' | 'sigma' | 'detection' = 'yara', save = false) =>
  secopsApi.post<ArgusGeneratedRule>(
    `/security/threats/${threatId}/generate-rule`,
    { rule_type: ruleType, save },
  );

export const generateRuleFromDescription = (threatDescription: string, ruleType: 'yara' | 'sigma' | 'detection' = 'yara') =>
  secopsApi.post<ArgusGeneratedRule>(
    '/security/threats/generate-rule',
    { threat_description: threatDescription, rule_type: ruleType },
  );
